import { useState, useMemo, useEffect, useRef } from "react";
import TextInput from "@leafygreen-ui/text-input";
import TextArea from "@leafygreen-ui/text-area";
import { Combobox, ComboboxOption } from "@leafygreen-ui/combobox";
import { Select, Option } from "@leafygreen-ui/select";
import Button from "@leafygreen-ui/button";
import Banner from "@leafygreen-ui/banner";
import Tooltip from "@leafygreen-ui/tooltip";
import IconButton from "@leafygreen-ui/icon-button";
import Icon from "@leafygreen-ui/icon";
import Code, { Language } from "@leafygreen-ui/code";
import ConfirmationModal from "@leafygreen-ui/confirmation-modal";
import { Label, Description, Body } from "@leafygreen-ui/typography";
import { css } from "@leafygreen-ui/emotion";

import {
  getTimeZones,
  formatUtcOffset,
  getTimeZoneOffsetMs,
  zonedComponentsToUtc,
  utcToZonedDatetimeLocal,
  parseDatetimeLocal,
  toDatetimeLocal,
} from "./timezoneUtils";

const USER_LIMIT_MAX = 3000;
const START_MAX_MONTHS_OUT = 6;

const DEFAULT_TITLE = "Foobar Developer Day";
const DEFAULT_DESCRIPTION = "A 1:many in person event at Pallet Town.";

const formGroup = css`
  margin-top: 16px;
  max-width: 520px;
`;

const trackHeaderRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const trackHeaderActions = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const environmentSelect = css`
  min-width: 140px;
`;

// Selected tracks are shown in the reorderable list below instead -- suppress the
// Combobox's own inline chips (still needed for multiselect + checkbox rendering)
// so they aren't shown twice.
const hideChips = css`
  [data-testid="lg-combobox-chip"] {
    display: none;
  }
`;

const trackList = css`
  border: 1px solid #C1C7C6;
  border-radius: 6px;
  margin-top: 8px;
  overflow: hidden;
`;

const trackRow = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: #fff;
  border-bottom: 1px solid #E8EDEB;

  &:last-child {
    border-bottom: none;
  }
`;

const trackRowDragging = css`
  opacity: 0.4;
`;

const dragHandle = css`
  display: flex;
  color: #889397;
  cursor: grab;
`;

const trackInfo = css`
  flex: 1;
  min-width: 0;
`;

const trackTitle = css`
  font-weight: 600;
`;

const trackSlug = css`
  color: #5C6C75;
  font-size: 0.8rem;
`;

const dateRow = css`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const dateInputStyle = css`
  padding: 10px;
  font-size: 14px;
  border: 1px solid #C1C7C6;
  border-radius: 6px;
  flex: 1;
`;

const labelWithInfo = css`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
`;

const resultArea = css`
  margin-top: 16px;
  max-width: 720px;
`;

const urlField = css`
  margin-top: 16px;
  width: 100%;
`;

const urlDisplay = css`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  padding: 8px 8px 8px 12px;
  border: 1px solid #C1C7C6;
  border-radius: 6px;
  background: #F9FBFA;
`;

const urlText = css`
  flex: 1;
  min-width: 0;
  font-family: monospace;
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const largerText = css`
  font-size: 18px;
`;

const confirmTrackList = css`
  margin: 8px 0;
  padding-left: 20px;
`;

const codeBlock = css`
  margin-top: 8px;
  max-height: 420px;
  overflow: auto;
`;

function detectZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

function defaultStartAt() {
  const start = new Date(Date.now() + 120000);
  start.setSeconds(0, 0);
  return toDatetimeLocal(start);
}

function InfoTooltip({ label, children }) {
  return (
    <Tooltip
      trigger={
        <IconButton aria-label={`About ${label}`}>
          <Icon glyph="InfoWithCircle" size="small" />
        </IconButton>
      }
    >
      {children}
    </Tooltip>
  );
}

export default function CreateInviteForm({ tracks, tracksLoading, onRefreshTracks, onDryRun, onCreate, organizationSlug, environment, environments, internalOnlyEnvironments, onEnvironmentChange }) {
  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [description, setDescription] = useState(DEFAULT_DESCRIPTION);
  const [internalNote, setInternalNote] = useState("");
  const [selectedTrackIds, setSelectedTrackIds] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [zone, setZone] = useState(detectZone);
  const [startAt, setStartAt] = useState(defaultStartAt);
  const [userLimit, setUserLimit] = useState(300);
  const [running, setRunning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [previewPayload, setPreviewPayload] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [urlCopied, setUrlCopied] = useState(false);

  // Track IDs belong to a single Instruqt org, so a previous environment's
  // selection never applies to the next one -- clear it on every switch
  // (but not on the initial load, when prevEnvironmentRef starts out null).
  const prevEnvironmentRef = useRef(environment);
  useEffect(() => {
    if (prevEnvironmentRef.current && environment && environment !== prevEnvironmentRef.current) {
      setSelectedTrackIds([]);
    }
    prevEnvironmentRef.current = environment;
  }, [environment]);

  const zones = useMemo(() => {
    return getTimeZones().map((z) => ({
      id: z,
      name: z,
      offset: formatUtcOffset(getTimeZoneOffsetMs(new Date(), z) / 60000),
    }));
  }, []);

  // Defensive: if the API ever returns something other than an array (e.g. an
  // unexpected error shape), fall back to an empty list rather than crashing
  // the whole form on .forEach/.sort.
  const safeTracks = Array.isArray(tracks) ? tracks : [];
  if (!Array.isArray(tracks)) {
    console.error("[Instruqt Invites] CreateInviteForm received a non-array `tracks` prop:", tracks);
  }

  const tracksById = useMemo(() => {
    const map = {};
    safeTracks.forEach((t) => { map[t.id] = t; });
    return map;
  }, [safeTracks]);

  const orderedSelectedTracks = selectedTrackIds.map((id) => tracksById[id]).filter(Boolean);
  const sortedTracks = useMemo(() => {
    return [...safeTracks].sort((a, b) => a.title.localeCompare(b.title));
  }, [safeTracks]);

  const safeEnvironments = environments?.length ? environments : ["prod", "dev"];
  const safeInternalOnlyEnvironments = Array.isArray(internalOnlyEnvironments) ? internalOnlyEnvironments : [];
  const isInternalOnly = safeInternalOnlyEnvironments.includes(environment);

  function handleTitleFocus() {
    if (title === DEFAULT_TITLE) setTitle("");
  }

  function handleDescriptionFocus() {
    if (description === DEFAULT_DESCRIPTION) setDescription("");
  }

  function handleTrackSelectionChange(newValue) {
    // Preserve the user's drag-reordered sequence for tracks that stay selected,
    // and append newly-checked tracks at the end -- don't just adopt whatever
    // order the Combobox itself returns (it doesn't know about our drag order).
    const newSet = new Set(newValue);
    const stillSelected = selectedTrackIds.filter((id) => newSet.has(id));
    const added = newValue.filter((id) => !selectedTrackIds.includes(id));
    setSelectedTrackIds([...stillSelected, ...added]);
  }

  function handleRemoveTrack(id) {
    setSelectedTrackIds(selectedTrackIds.filter((tid) => tid !== id));
  }

  function handleTrackDragStart(index) {
    setDraggedIndex(index);
  }

  function handleTrackDragOver(e) {
    e.preventDefault();
  }

  function handleTrackDrop(dropIndex) {
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }
    const next = [...selectedTrackIds];
    const [moved] = next.splice(draggedIndex, 1);
    next.splice(dropIndex, 0, moved);
    setSelectedTrackIds(next);
    setDraggedIndex(null);
  }

  function handleNowClick() {
    const nowCeil = new Date(Math.ceil(Date.now() / 60000) * 60000);
    setStartAt(utcToZonedDatetimeLocal(nowCeil, zone));
  }

  function handleCopyUrl(url) {
    navigator.clipboard.writeText(url).then(
      () => {
        setUrlCopied(true);
        setTimeout(() => setUrlCopied(false), 1500);
      },
      (e) => {
        console.error("[Instruqt Invites] Could not copy the invite URL to the clipboard:", e);
      }
    );
  }

  function buildPayload() {
    if (!title.trim()) {
      return { error: "Title is required" };
    }
    if (!description.trim()) {
      return { error: "Description is required" };
    }
    if (!selectedTrackIds.length) {
      return { error: "Select at least one track" };
    }
    if (!startAt) {
      return { error: "Pick a start date/time" };
    }
    const { y, mo, d, h, mi } = parseDatetimeLocal(startAt);
    const startInstant = zonedComponentsToUtc(y, mo, d, h, mi, zone);
    if (startInstant.getTime() <= Date.now()) {
      return { error: "Pick a start date/time in the future" };
    }
    const maxStart = new Date();
    maxStart.setMonth(maxStart.getMonth() + START_MAX_MONTHS_OUT);
    if (startInstant.getTime() > maxStart.getTime()) {
      return { error: `Pick a start date/time within the next ${START_MAX_MONTHS_OUT} months` };
    }
    return {
      payload: {
        title,
        description,
        internalNote,
        trackIds: selectedTrackIds,
        startsAt: startInstant.toISOString(),
        userLimit,
      }
    };
  }

  // Clicking "Create Invite" doesn't create anything yet -- it runs a dry run first
  // and, if that succeeds, opens a confirmation dialog showing exactly what would be
  // created. The real create only happens if the user confirms from that dialog.
  async function handlePrimaryClick() {
    const { payload, error: validationError } = buildPayload();
    if (validationError) {
      setError(validationError);
      setResult(null);
      return;
    }
    setError(null);
    setResult(null);
    setRunning(true);
    try {
      const response = await onDryRun(payload);
      if (response?.error) {
        console.error(
          `[Instruqt Invites] Dry-run preview failed: ${response.error}\n` +
          "If this mentions missing config, check this environment's INSTRUQT_<ENV>_API_KEY / " +
          "INSTRUQT_<ENV>_TEAM_NAME env vars, plus INSTRUQT_ACCESS_CODE, PASSKEY_LAMBDA_ENDPOINT, " +
          "and MODEL_LAMBDA_ENDPOINT, on the api service."
        );
        setError(response.error);
      } else {
        setPendingPayload(payload);
        setPreviewPayload(response.payload);
        setConfirming(true);
      }
    } catch (e) {
      console.error("[Instruqt Invites] Dry-run preview request failed:", e);
      setError(e.message || "Request failed");
    }
    setRunning(false);
  }

  async function handleConfirmCreate() {
    setConfirming(false);
    setRunning(true);
    try {
      const response = await onCreate(pendingPayload);
      if (response?.error) {
        console.error(`[Instruqt Invites] Create invite failed: ${response.error}`);
        setError(response.error);
      } else {
        setResult(response);
      }
    } catch (e) {
      console.error("[Instruqt Invites] Create invite request failed:", e);
      setError(e.message || "Request failed");
    }
    setPendingPayload(null);
    setPreviewPayload(null);
    setRunning(false);
  }

  function handleCancelConfirm() {
    setConfirming(false);
    setPendingPayload(null);
    setPreviewPayload(null);
  }

  return (
    <div>
      <div className={formGroup}>
        <TextInput
          label="Event Name"
          description={`${title.length}/120 chars`}
          value={title}
          maxLength={120}
          onFocus={handleTitleFocus}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className={formGroup}>
        <div className={labelWithInfo}>
          <Label htmlFor="description" id="descriptionLabel">Description</Label>
          <InfoTooltip label="description">
            Visible to invitees.
          </InfoTooltip>
        </div>
        <TextArea
          id="description"
          aria-labelledby="descriptionLabel"
          description={`${description.length}/300 chars`}
          value={description}
          maxLength={300}
          onFocus={handleDescriptionFocus}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className={formGroup}>
        <div className={labelWithInfo}>
          <Label htmlFor="internalNote" id="internalNoteLabel">Internal note</Label>
          <InfoTooltip label="internal note">
            For your own reference only. Never shown to invitees.
          </InfoTooltip>
        </div>
        <TextArea
          id="internalNote"
          aria-labelledby="internalNoteLabel"
          description={`${internalNote.length}/500 chars`}
          value={internalNote}
          maxLength={500}
          onChange={(e) => setInternalNote(e.target.value)}
        />
      </div>
      <div className={formGroup}>
        <div className={trackHeaderRow}>
          <Label htmlFor="trackPicker">
            {`Tracks${selectedTrackIds.length ? ` (${selectedTrackIds.length} selected)` : ""}`}
          </Label>
          <div className={trackHeaderActions}>
            {environment && (
              <div className={environmentSelect}>
                <Select
                  aria-label="Environment"
                  size="xsmall"
                  value={environment}
                  onChange={onEnvironmentChange}
                  allowDeselect={false}
                >
                  {safeEnvironments.map((env) => (
                    <Option key={env} value={env}>{env === "prod" ? "Production" : "Development"}</Option>
                  ))}
                </Select>
              </div>
            )}
            <Button size="xsmall" type="button" onClick={onRefreshTracks} disabled={tracksLoading}>
              {tracksLoading ? "Refreshing..." : "Refresh tracks"}
            </Button>
          </div>
        </div>
        {organizationSlug && (
          <Description>Instruqt org: {organizationSlug}</Description>
        )}
        {isInternalOnly && (
          <Banner variant="danger">
            Dev environment is for INTERNAL use only. Do not share invites created here with
            external learners.
          </Banner>
        )}
        <Banner variant="info">
          A passkey is automatically generated and injected into the selected track(s)
          as a runtime environment variable.
        </Banner>
        <Combobox
          id="trackPicker"
          className={hideChips}
          placeholder="Select..."
          multiselect
          chipTruncationLocation="none"
          value={selectedTrackIds}
          onChange={handleTrackSelectionChange}
        >
          {sortedTracks.map((t) => (
            <ComboboxOption
              key={t.id}
              value={t.id}
              displayName={`${t.title} (${t.slug})`}
            />
          ))}
        </Combobox>
        <Description>Content shown to invitees in the order you arrange below — drag to reorder.</Description>
        {orderedSelectedTracks.length > 0 && (
          <div className={trackList}>
            {orderedSelectedTracks.map((t, index) => (
              <div
                key={t.id}
                className={draggedIndex === index ? `${trackRow} ${trackRowDragging}` : trackRow}
                draggable
                onDragStart={() => handleTrackDragStart(index)}
                onDragOver={handleTrackDragOver}
                onDrop={() => handleTrackDrop(index)}
                onDragEnd={() => setDraggedIndex(null)}
              >
                <span className={dragHandle} aria-hidden="true">
                  <Icon glyph="Drag" />
                </span>
                <div className={trackInfo}>
                  <div className={trackTitle}>{t.title}</div>
                  <div className={trackSlug}>{t.slug}</div>
                </div>
                <IconButton aria-label={`Remove ${t.title}`} onClick={() => handleRemoveTrack(t.id)}>
                  <Icon glyph="X" />
                </IconButton>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className={formGroup}>
        <Label htmlFor="startAt">Start date &amp; time</Label>
        <div className={dateRow}>
          <Button type="button" onClick={handleNowClick}>Now</Button>
          <input
            id="startAt"
            type="datetime-local"
            className={dateInputStyle}
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
          />
          <Select
            label="Timezone"
            value={zone}
            onChange={(value) => setZone(value)}
            allowDeselect={false}
          >
            {zones.map((z) => (
              <Option key={z.id} value={z.id}>
                {`${z.name} (${z.offset})`}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <div className={formGroup}>
        <div className={labelWithInfo}>
          <Label htmlFor="userLimit" id="userLimitLabel">User limit</Label>
          <InfoTooltip label="user limit">
            Maximum number of unique people who can claim this invite (max {USER_LIMIT_MAX}).
          </InfoTooltip>
        </div>
        <TextInput
          id="userLimit"
          aria-labelledby="userLimitLabel"
          type="number"
          value={String(userLimit)}
          onChange={(e) => setUserLimit(Number(e.target.value))}
        />
      </div>
      <div className={formGroup}>
        <Description>
          Once created, the Invite URL will be shown here
        </Description>
        <Button variant="primary" onClick={handlePrimaryClick} disabled={running}>
          {running ? "Checking..." : "Create Invite (Preview first)"}
        </Button>
      </div>
      {error && (
        <div className={formGroup}>
          <Banner variant="danger">{error}</Banner>
        </div>
      )}
      {result && (
        <div className={resultArea}>
          <Banner variant="success">{`Created invite "${result.invite?.title}".`}</Banner>
          {result.inviteUrl && (
            <div className={urlField}>
              <Label htmlFor="inviteUrlText">Invite URL</Label>
              <Banner variant="warning">Copy this URL now (it's not stored locally, visit Instruqt for editing).</Banner>
              <div className={urlDisplay}>
                <span id="inviteUrlText" className={urlText}>{result.inviteUrl}</span>
                <IconButton aria-label="Copy Invite URL" onClick={() => handleCopyUrl(result.inviteUrl)}>
                  <Icon glyph={urlCopied ? "Checkmark" : "Copy"} />
                </IconButton>
                <IconButton
                  aria-label="Open Invite URL in a new tab"
                  onClick={() => window.open(result.inviteUrl, "_blank", "noopener,noreferrer")}
                >
                  <Icon glyph="OpenNewTab" />
                </IconButton>
                {result.editUrl && (
                  <IconButton
                    aria-label="Edit this invite"
                    onClick={() => window.open(result.editUrl, "_blank", "noopener,noreferrer")}
                  >
                    <Icon glyph="Edit" />
                  </IconButton>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      <div className={formGroup}>
        <Body className={largerText}>
          Looking to create a Forever invite (never expires)? This tool doesn't support
          that. Reach out in #ask-devrel.
        </Body>
      </div>
      <ConfirmationModal
        open={confirming}
        title={`Confirm "${title}" Invite:`}
        buttonText="Create Invite"
        variant="primary"
        onConfirm={handleConfirmCreate}
        onCancel={handleCancelConfirm}
      >
        <Body>Tracks:</Body>
        <ul className={confirmTrackList}>
          {orderedSelectedTracks.map((t) => (
            <li key={t.id}>{t.title} ({t.slug})</li>
          ))}
        </ul>
        {previewPayload && (
          <div className={codeBlock}>
            <Code language={Language.Json} copyable>
              {JSON.stringify(previewPayload, null, 2)}
            </Code>
          </div>
        )}
      </ConfirmationModal>
    </div>
  );
}
